/* eslint-disable no-useless-constructor */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { actionsAdvancedFilters } from '../actions';
import { VALUE_FOR_NO_FILTER } from '../FiltersDefinition';

function Checkbox(props) {
  const {
    group,
    filters,
    value,
    text,
    children,
    definition,
    updateFilterState,
    isGroupToggle,
    groupName,
    toggleValues,
    hierarchyDepth,
  } = props;
  const checkboxRef = React.useRef(null);

  const isCheckedState = state => state === true || state === 'true';

  const getMyStateFromProps = () => {
    if (definition.type === 'radio') {
      if (value === VALUE_FOR_NO_FILTER) {
        const hasSelectedOption = filters.some(f => f.group === group && f.value !== VALUE_FOR_NO_FILTER && isCheckedState(f.state));
        return !hasSelectedOption;
      }

      const selectedOption = filters.find(f => f.group === group && f.value === value);
      return isCheckedState(selectedOption?.state);
    }

    let myState;
    filters.forEach(e => {
      if (e.group === group && e.value === value) myState = e.state;
    });

    return myState;
  };

  const getGroupValues = () => {
    if (toggleValues) {
      return toggleValues;
    }
    return definition.values
      .filter(v => v.groupedOptionName === groupName)
      .map(v => v.value);
  };

  const getGroupToggleState = () => {
    if (!isGroupToggle || (!groupName && !toggleValues)) {
      return { checked: false, indeterminate: false };
    }

    const groupValues = getGroupValues();
    const selectedValues = new Set(
      filters
        .filter(filter => filter.group === group && isCheckedState(filter.state))
        .map(filter => filter.value),
    );
    const selectedCount = groupValues.filter(groupValue => selectedValues.has(groupValue)).length;

    return {
      checked: groupValues.length > 0 && selectedCount === groupValues.length,
      indeterminate: selectedCount > 0 && selectedCount < groupValues.length,
    };
  };

  const groupToggleState = getGroupToggleState();

  React.useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = groupToggleState.indeterminate;
    }
  }, [groupToggleState.indeterminate]);

  const onClick = () => {
    if (isGroupToggle) {
      const newState = !groupToggleState.checked;
      const groupValues = getGroupValues();
      
      // Update each filter that belongs to this specific organization group
      groupValues.forEach(filterValue => {
        updateFilterState({
          group,
          value: filterValue,
          state: newState,
        });
      });
      
    } else {
      const checked = getMyStateFromProps();

      const event = {
        group,
        value,
        state: definition.type ? true : !checked,
      };
      updateFilterState(event, definition.type, value === 'all' ? definition.values.map(v => ({ group, value: v.value })) : undefined);
    }
  };

  const checked = isGroupToggle ? groupToggleState.checked : getMyStateFromProps();

  // Check if this is the last element in its group
  const isLastInGroup = () => {
    if (isGroupToggle) return false; // Group toggles are never last in group
    
    // Find the current element in definition.values
    const currentIndex = definition.values.findIndex(v => v.value === value);
    if (currentIndex === -1) return false;
    
    const currentElement = definition.values[currentIndex];
    
    // If element doesn't have a group, it's not part of a group
    if (!currentElement.groupedOptionName) return false;
    
    // Check if there's any element after this one with the same groupedOptionName
    for (let i = currentIndex + 1; i < definition.values.length; i += 1) {
      if (definition.values[i].groupedOptionName === currentElement.groupedOptionName) {
        return false; // Found another element in the same group
      }
    }
    
    return true; // No more elements with same groupedOptionName found
  };

  return (
    <div
      className={`list-item ${isGroupToggle ? 'group-toggle' : ''} ${hierarchyDepth !== undefined ? 'hierarchy-item' : ''} ${isLastInGroup() || value === "all" ? 'last-element' : ''}`}
      data-testid={`advanced-filter-option[${group}][${text}]`}
      onClick={onClick}
      style={hierarchyDepth !== undefined ? { '--advanced-filter-hierarchy-depth': hierarchyDepth } : undefined}
    >
      {children ? <div className="label-list-item">{children}</div> : null}
      {text.length !== 0 ? <div className="label-list-item">{text}</div> : null}
      <input ref={checkboxRef} type={definition.type || "checkbox"} checked={checked} onChange={() => {}} />
    </div>
  );
}

Checkbox.propTypes = {
  group: PropTypes.string.isRequired,
  updateFilterState: PropTypes.func.isRequired,
  text: PropTypes.string.isRequired,
  filters: PropTypes.array.isRequired,
  value: PropTypes.string.isRequired,
  children: PropTypes.any,
  definition: PropTypes.object.isRequired,
  isGroupToggle: PropTypes.bool,
  groupName: PropTypes.string,
  toggleValues: PropTypes.arrayOf(PropTypes.string),
  hierarchyDepth: PropTypes.number,
};

const mapStateToProps = state => {
  return {
    filters: state.entities.advancedFilters.filters.items,
  };
};

const mapDispatchToProps = dispatch => ({
  updateFilterState: (data, filterType, allFilters) => dispatch(actionsAdvancedFilters.updateFilterState(data, filterType, allFilters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Checkbox);
